import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Platform from "@/pages/Platform";
import Dashboard from "@/pages/Dashboard";
import Accounts from "@/pages/Accounts";
import { Customers, Vendors } from "@/pages/Contacts";
import Products from "@/pages/Products";
import { Invoices, Bills, Estimates, Recurring, PurchaseOrders } from "@/pages/Documents";
import Expenses from "@/pages/Expenses";
import JournalEntries from "@/pages/JournalEntries";
import Payroll from "@/pages/Payroll";
import Reports from "@/pages/Reports";
import InvoiceView from "@/pages/InvoiceView";
import Settings from "@/pages/Settings";
import Banking from "@/pages/Banking";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="grid min-h-screen place-items-center text-sm text-slate-500">Loading NeroBooks…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.platform_role === "super") return <Navigate to="/platform" replace />;
  return <Layout>{children}</Layout>;
};

const SuperOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="grid min-h-screen place-items-center text-sm text-slate-500">Loading NeroBooks…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.platform_role !== "super") return <Navigate to="/" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="grid min-h-screen place-items-center text-sm text-slate-500">Loading NeroBooks…</div>;
  if (user) return <Navigate to={user.platform_role === "super" ? "/platform" : "/"} replace />;
  return children;
};

const ROUTES = [
  ["/", Dashboard], ["/invoices", Invoices], ["/invoices/:invoiceId", InvoiceView],
  ["/estimates", Estimates], ["/estimates/:invoiceId", () => <InvoiceView kind="estimates" />],
  ["/recurring", Recurring],
  ["/purchase-orders", PurchaseOrders], ["/purchase-orders/:invoiceId", () => <InvoiceView kind="purchase-orders" />],
  ["/bills", Bills], ["/expenses", Expenses], ["/banking", Banking],
  ["/customers", Customers], ["/vendors", Vendors], ["/products", Products],
  ["/accounts", Accounts], ["/journal", JournalEntries], ["/payroll", Payroll],
  ["/reports", Reports], ["/settings", Settings],
];

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/platform" element={<SuperOnly><Platform /></SuperOnly>} />
          {ROUTES.map(([path, Component]) => (
            <Route key={path} path={path} element={<Protected><Component /></Protected>} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
